// AmsOverview.tsx — the arrears dashboard.
//
// Two things on this screen are deliberate and should not be "tidied up":
//
//   1. THE MND FIGURE CARRIES ITS DENOMINATOR. The band is measured on accounts beyond 90 days
//      against the WHOLE account base, not against the arrears population. Rendering "3.98%" alone
//      invites someone to quote it against the wrong base, which is how a town reports 3% when the
//      real figure is twenty. The numerator and denominator are on screen, always.
//
//   2. STALENESS IS VISIBLE. `figuresAsOf` says when TCMS was last read. Nothing here is safe to
//      act on without a fresh read, and the server enforces that separately — but an officer
//      should be able to see it rather than trust it.
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../auth/AuthContext'
import { api, ApiError, type AmsOverview as Data } from '../../lib/api'
import { ageOf, BUCKETS, CATEGORIES, DRIVERS, moneyWhole } from '../../lib/format'

const DIVISIONS = ['NSC', 'NSE', 'CP', 'NSL', 'NSS']

export default function AmsOverview() {
  const { me } = useAuth()
  const org = me?.org?.id
  const [division, setDivision] = useState<string | undefined>()
  const [data, setData] = useState<Data | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!org) return
    let off = false
    setData(null); setError(null)
    api.ams.overview(org, division)
      .then(d => { if (!off) setData(d) })
      // A failed load is reported, never rendered as zeros (TD-034). "No arrears" and "we could
      // not read the arrears" are different facts, and only one of them is good news.
      .catch((e: unknown) => { if (!off) setError(e instanceof ApiError ? e.detail : String(e)) })
    return () => { off = true }
  }, [org, division])

  if (error) return <div className="page-state page-state-error">Could not load: {error}</div>
  if (!data) return <div className="page-state">Loading…</div>

  const m = data.mndBasis
  const band = m.pct <= m.targetPct ? 'ok' : m.pct < m.greenBandPct ? 'warn' : 'bad'
  const cat = (k: string) => data.categories.find(c => c.key === k)?.count ?? 0
  const bucket = (k: string) => data.buckets.find(b => b.bucket === k)

  return (
    <div className="ams">
      <header className="ams-head">
        <div>
          <h2>Arrears overview</h2>
          <p className="muted">
            Case data from this system; balances from TCMS, read {ageOf(data.figuresAsOf)}.
          </p>
        </div>
        <div className="chips">
          <button className={`chip ${!division ? 'on' : ''}`} onClick={() => setDivision(undefined)}>
            All divisions
          </button>
          {DIVISIONS.map(d => (
            <button key={d} className={`chip ${division === d ? 'on' : ''}`}
                    onClick={() => setDivision(d)}>{d}</button>
          ))}
        </div>
      </header>

      <div className="grid-4">
        <div className="card kpi">
          <div className="kpi-label">Accounts in arrears</div>
          <div className="kpi-n">{data.accountsInArrears.toLocaleString()}</div>
          <div className="muted sm">of {data.totalAccounts.toLocaleString()} accounts</div>
        </div>
        <div className="card kpi">
          <div className="kpi-label">Total owed</div>
          <div className="kpi-n">{moneyWhole(data.arrearsCents)}</div>
        </div>
        <div className={`card kpi kpi-${band}`}>
          <div className="kpi-label">MND measure</div>
          <div className="kpi-n">{m.pct}%</div>
          {/* The denominator, on screen, every time. */}
          <div className="muted sm">
            {m.numerator.toLocaleString()} accounts over 90 days ÷{' '}
            {m.denominator.toLocaleString()} total
          </div>
          <div className="muted sm">Green band under {m.greenBandPct}% · target {m.targetPct}%</div>
        </div>
        <div className="card kpi">
          <div className="kpi-label">Over 90 days</div>
          <div className="kpi-n">{(bucket('d90')?.count ?? 0).toLocaleString()}</div>
          <div className="muted sm">{moneyWhole(bucket('d90')?.cents ?? 0)} outstanding</div>
        </div>
      </div>

      <div className="grid-2">
        <section className="card">
          <h3>Where accounts sit</h3>
          <p className="muted sm">Days since the oldest unpaid charge — the basis any letter rests on.</p>
          <div className="stages">
            {(['d30', 'd60', 'd90'] as const).map(k => {
              const b = bucket(k)
              return (
                <Link key={k} to={`/ams/queue?bucket=${k}`} className={`stage stage-${k}`}>
                  <div className="stage-n">{(b?.count ?? 0).toLocaleString()}</div>
                  <b>{BUCKETS[k]}</b>
                  <span className="muted sm">{moneyWhole(b?.cents ?? 0)}</span>
                </Link>
              )
            })}
          </div>

          <h3 style={{ marginTop: 22 }}>By resident category</h3>
          <p className="muted sm">Category decides the treatment, not the amount owed.</p>
          <ul className="bars">
            {['1', '2', '3', '4', 'unsegmented'].map(k => {
              const n = cat(k)
              const pct = data.accountsInArrears ? (100 * n) / data.accountsInArrears : 0
              return (
                <li key={k}>
                  <Link to={`/ams/queue?category=${k}`}>{CATEGORIES[k]}</Link>
                  <div className="bar"><i style={{ width: `${pct}%` }} className={`c${k}`} /></div>
                  <span>{n.toLocaleString()}</span>
                </li>
              )
            })}
          </ul>
        </section>

        <section className="card">
          <h3>Needs attention today</h3>
          <ul className="notices">
            <li className="notice stop">
              <b>{data.attention.approvals_waiting} approvals waiting.</b>{' '}
              <Link to="/ams/approvals">Review</Link>
            </li>
            <li className="notice">
              <b>{data.attention.promises_broken} promises to pay broken.</b> Queued for follow-up.
            </li>
            <li className="notice ok">
              <b>{data.attention.evidence_expiring} hardship evidence items expire within 14 days.</b>{' '}
              Expiry raises a review — it never resumes enforcement on its own.
            </li>
            {/* "Triggered, no confirmation" is the failure mode of trigger-and-confirm: we asked
                TCMS to send and do not know whether a resident received it. It gets its own line. */}
            {data.attention.sends_unconfirmed > 0 && (
              <li className="notice stop">
                <b>{data.attention.sends_unconfirmed} sends unconfirmed by TCMS.</b>{' '}
                We asked, and do not know whether they went out.
              </li>
            )}
          </ul>

          <h3 style={{ marginTop: 22 }}>Hardship drivers</h3>
          <ul className="bars">
            {data.hardshipDrivers.map(d => {
              const tot = data.hardshipDrivers.reduce((s, x) => s + x.count, 0) || 1
              return (
                <li key={d.code}>
                  <span>{d.code} {DRIVERS[d.code] ?? ''}</span>
                  <div className="bar"><i style={{ width: `${(100 * d.count) / tot}%` }} className="c1" /></div>
                  <span>{d.count}</span>
                </li>
              )
            })}
          </ul>
        </section>
      </div>

      <section className="card">
        <h3>By division</h3>
        <table className="tbl">
          {/* Figures are right-aligned so the thousands line up down the column. Left-aligned
              counts of differing width cannot be compared by eye, which is the only reason this
              table exists. */}
          <thead><tr><th>Division</th><th className="num">In arrears</th>
            <th className="num">Over 90 days</th><th /></tr></thead>
          <tbody>
            {data.divisions.map(d => (
              <tr key={d.division}>
                <td><b>{d.division}</b></td>
                <td className="num">{d.count.toLocaleString()}</td>
                <td className="num">{d.d90.toLocaleString()}</td>
                <td className="right"><Link to={`/ams/queue?division=${d.division}`}>Open queue</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  )
}
